import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import DataTable from '../../../components/orizon-ui/DataTable';
import StatusPill from '../../../components/orizon-ui/StatusPill';
import { NumberField, SelectField, TextareaField, ToggleField } from '../../../components/orizon-ui/FormField';
import { useToast } from '../../../components/orizon-ui/Toast';
import { useConfirm } from '../../../components/orizon-ui/ConfirmDialog';
import FormModal, { useFormSalva, useSporco } from '../../../components/gru/FormModal';
import { formattaQta } from '../../../components/gru/scorta';
import { gruOpzioni, gruCrea, gruAggiorna, gruElimina, mapGruError } from '../../../lib/services/gruService';
import { useListaGru } from '../../../components/gru/useListaGru';

const decimaliDi = (testo) => {
  const s = String(testo ?? '').replace(',', '.');
  const i = s.indexOf('.');
  return i === -1 ? 0 : s.length - i - 1;
};

const etichettaComponente = (c) => `${c.gru_codice} · ${c.reparto_label}${c.posizione ? ` ${c.posizione}` : ''}${c.etichetta ? ` — ${c.etichetta}` : ''}`;

// ─── Form: associa articolo a un componente ──────────────────────────────────

const FormCompatibilita = ({ record, articoloId, unitaLabel, unitaDecimali, onClose, onSaved }) => {
  const modifica = Boolean(record);
  const toast = useToast();
  const [componenti, setComponenti] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [componenteId, setComponenteId] = useState(record?.componente_id ?? '');
  const [quantita, setQuantita] = useState(String(record?.quantita ?? 1));
  const [primario, setPrimario] = useState(record?.primario ?? false);
  const [nota, setNota] = useState(record?.note ?? '');
  const { salvando, errori, impostaErrori, pulisci, salva } = useFormSalva();
  const sporco = useSporco({ componenteId, quantita, primario, nota });

  useEffect(() => {
    let annullato = false;
    gruOpzioni('componenti')
      .then((righe) => {
        if (annullato) return;
        setComponenti(righe);
        setCaricamento(false);
      })
      .catch((e) => {
        if (annullato) return;
        toast.error(mapGruError(e).message);
        setCaricamento(false);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const opzioni = useMemo(() => componenti
    .filter((c) => c.attivo || c.id === record?.componente_id)
    .map((c) => ({ value: c.id, label: `${etichettaComponente(c)}${c.attivo ? '' : ' (disattivato)'}` })),
  [componenti, record]);

  const invia = async () => {
    const nuovi = {};
    if (!componenteId) nuovi.componente_id = 'Obbligatorio';
    const q = Number(String(quantita).replace(',', '.'));
    if (!Number.isFinite(q) || q <= 0) nuovi.quantita = 'Deve essere maggiore di 0';
    else if (decimaliDi(quantita) > unitaDecimali) {
      nuovi.quantita = unitaDecimali === 0 ? 'Solo quantità intere' : `Massimo ${unitaDecimali} decimali`;
    }
    impostaErrori(nuovi);
    if (Object.keys(nuovi).length) return;

    const payload = {
      quantita: q,
      primario,
      note: nota.trim() === '' ? null : nota.trim(),
      ...(modifica ? {} : { articolo_id: articoloId, componente_id: componenteId }),
    };

    await salva(
      () => (modifica ? gruAggiorna('compatibilita', record.id, payload) : gruCrea('compatibilita', payload)),
      modifica ? 'Compatibilità aggiornata.' : 'Articolo associato al componente.',
      onSaved,
    );
  };

  return (
    <FormModal
      titolo={modifica ? 'Modifica compatibilità' : 'Associa a un componente'}
      etichetta="Articolo · Compatibilità"
      larghezza="md"
      sporco={sporco}
      salvando={salvando}
      onClose={onClose}
      onSubmit={invia}
    >
      <SelectField
        label="Componente" name="componente_id" value={componenteId}
        onChange={(v) => { setComponenteId(v); pulisci('componente_id'); }}
        opzioni={opzioni} obbligatorio errore={errori.componente_id}
        disabilitato={caricamento || modifica}
        suggerimento={modifica ? 'Il componente non si cambia: elimina l\'associazione e creane una nuova.' : undefined}
      />
      <NumberField
        label="Quantità per componente" name="quantita" value={quantita}
        onChange={(v) => { setQuantita(v); pulisci('quantita'); }}
        suffisso={unitaLabel} obbligatorio errore={errori.quantita} min={0}
        suggerimento="Quanti pezzi di questo articolo servono per ciascun componente."
      />
      <ToggleField
        label="Articolo primario"
        sublabel={primario ? 'È l\'articolo di riferimento per questo componente' : 'Articolo alternativo'}
        checked={primario}
        onChange={setPrimario}
      />
      <TextareaField label="Nota" name="note" value={nota} onChange={setNota} righe={2} errore={errori.note} />
    </FormModal>
  );
};

// ─── Pannello ────────────────────────────────────────────────────────────────

const AzioneBtn = ({ icona, titolo, onClick, pericolo = false }) => (
  <button
    type="button"
    title={titolo}
    aria-label={titolo}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    className={`w-9 h-9 rounded-xl border bg-muted/30 border-border/40 text-muted-foreground flex items-center justify-center transition-all ${
      pericolo
        ? 'hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/30'
        : 'hover:bg-primary hover:text-primary-foreground hover:border-primary'
    }`}
  >
    <Icon name={icona} size={15} />
  </button>
);

/** Elenco dei componenti compatibili con un articolo + aggiunta, modifica ed eliminazione dei collegamenti. */
const CompatibilitaPanel = ({ articolo, onCambiato, onModaleChange }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const [modale, setModale] = useState(null); // null | { record }
  const lista = useListaGru({
    entita: 'compatibilita',
    filtri: { articolo_id: articolo.id },
    ordineIniziale: { key: 'gru_codice', direction: 'asc' },
    perPagina: 10,
  });

  // avvisa il modal contenitore: finché il form è aperto, Esc/backdrop non devono chiudere anche il dettaglio
  useEffect(() => {
    onModaleChange?.(modale !== null);
    return () => onModaleChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modale]);

  const elimina = async (riga) => {
    const ok = await confirm({
      titolo: 'Rimuovere l\'associazione?',
      descrizione: `L'articolo non sarà più indicato come compatibile con ${riga.gru_codice} · ${riga.reparto_label}${riga.posizione ? ` ${riga.posizione}` : ''}.`,
      confermaLabel: 'Rimuovi',
    });
    if (!ok) return;
    try {
      await gruElimina('compatibilita', riga.id);
      toast.success('Associazione rimossa.');
      lista.ricarica();
      onCambiato?.();
    } catch (e) {
      toast.error(mapGruError(e).message);
    }
  };

  const colonne = [
    { key: 'gru_codice', label: 'Gru', width: 110, render: (r) => <span className="font-mono text-[12px] font-bold text-foreground/80">{r.gru_codice}</span> },
    { key: 'reparto_label', label: 'Reparto', width: 120, render: (r) => <span className="text-[12px] font-black uppercase tracking-tight text-foreground/80">{r.reparto_label}</span> },
    { key: 'posizione', label: 'Pos.', width: 60, align: 'center', render: (r) => <span className="text-[12px] font-black text-primary/70">{r.posizione || '—'}</span> },
    { key: 'componente_etichetta', label: 'Etichetta', width: 180, render: (r) => <span className="text-[12px] font-medium text-muted-foreground">{r.componente_etichetta || '---'}</span> },
    {
      key: 'quantita', label: 'Q.tà', width: 90, align: 'center',
      render: (r) => <span className="text-[13px] font-black tabular-nums text-foreground/80">{formattaQta(r.quantita, articolo.unita_decimali)}</span>,
    },
    {
      key: 'primario', label: 'Primario', width: 100, align: 'center',
      render: (r) => (r.primario ? <StatusPill tono="ok" label="Primario" /> : <span className="text-[11px] font-bold text-muted-foreground/40">—</span>),
    },
    {
      key: '__azioni', label: '', width: 110, align: 'center', noSort: true,
      render: (r) => (
        <div className="flex items-center justify-center gap-2">
          <AzioneBtn icona="Pencil" titolo="Modifica" onClick={() => setModale({ record: r })} />
          <AzioneBtn icona="Trash2" titolo="Rimuovi" pericolo onClick={() => elimina(r)} />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setModale({ record: null })}
          className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-primary/10 hover:bg-primary/20 border border-primary/30 text-primary text-[11px] font-black uppercase tracking-widest transition-all"
        >
          <Icon name="Link2" size={14} />
          Associa componente
        </button>
      </div>

      <DataTable
        colonne={colonne}
        righe={lista.righe}
        totale={lista.totale}
        loading={lista.loading}
        pagina={lista.pagina}
        perPagina={lista.perPagina}
        onPagina={lista.setPagina}
        sort={lista.sort}
        onSort={lista.onSort}
        messaggioVuoto="Nessun componente associato a questo articolo"
        altezzaMax="max-h-[34dvh]"
        densita="compatta"
      />

      {modale && (
        <FormCompatibilita
          record={modale.record}
          articoloId={articolo.id}
          unitaLabel={articolo.unita_label}
          unitaDecimali={Number(articolo.unita_decimali ?? 0)}
          onClose={() => setModale(null)}
          onSaved={() => {
            setModale(null);
            lista.ricarica();
            onCambiato?.();
          }}
        />
      )}
    </div>
  );
};

export default CompatibilitaPanel;
